import { getCookie, setCookie } from '@/Utility/cookie';
import { TelemetryRunner } from './TelemetryRunner';
import { TelemetrySyncSession } from './syncSession';

export enum CookiesEnum {
  GA_CLIENT_ID = '_ga',
  ZN_NEW_VISITOR = 'zngly/__nvt',
  ZN_CLIENT_ID = 'zngly/__cid',
  ZN_LOGGED_IN = 'zngly/__liu',
}
export class CookieHandler {
  private static znClientId: string | null = null;
  private static znLoggedInUserId: string | undefined | null = undefined;
  private static hasInit = false;

  constructor() {
    CookieHandler.initCookies();
  }

  public static initCookies(): void {
    if (CookieHandler.hasInit) return;
    CookieHandler.hasInit = true;

    const znClientId = CookieHandler.getZnClientId();
    const loggedInUserId = CookieHandler.getLoggedInUserId();

    // get the ga config id
    const gaConfigId = window?.znRuntime?.gaConfigId;
    if (typeof gaConfigId === 'string' && gaConfigId?.length > 0) {
      // We set the gtag config on the client side from now on
      // This is to ensure that from the moment we send events, we have the correct user id's in place.
      // There was inconsitent behaviour when setting the user id on the server side, some events would have the user id and some would not
      gtag('config', gaConfigId, {
        user_id: `${loggedInUserId ?? ''}::${znClientId}`,
        user_properties: {
          cid: znClientId,
          uid: loggedInUserId,
        },
      });
    }
  }

  public static getLoggedInUserId(): string | null {
    if (CookieHandler.znLoggedInUserId !== undefined) {
      return CookieHandler.znLoggedInUserId;
    }

    const loggedInUserId = getCookie<string>(CookiesEnum.ZN_LOGGED_IN);

    const userId = window?.znRuntime?.userId ?? null;

    if (userId && userId !== loggedInUserId) {
      setCookie(CookiesEnum.ZN_LOGGED_IN, userId, {
        days: 365,
        domain: 'subDomain',
      });
    }

    const value = userId ?? loggedInUserId;
    CookieHandler.znLoggedInUserId = value;

    return value;
  }

  public static getZnClientId(): string {
    if (CookieHandler.znClientId && !!CookieHandler?.znClientId?.length) {
      return CookieHandler.znClientId;
    }

    const match = document.cookie.match(/_ga=(.+?);/);

    let clientId = '';

    if (!!match && match.length) {
      for (const item of match) {
        if (item.includes('GA') && !item.includes('_ga=')) {
          clientId = item;
          break;
        }
      }
    }

    if (clientId === '') {
      clientId = getCookie<string>(CookiesEnum.GA_CLIENT_ID) ?? '';
    }

    const znClientId = getCookie<string>('zngly/__cid');

    if (!clientId || clientId === '' || !znClientId || znClientId === '') {
      clientId = CookieHandler.generateGaCookie();
    }

    if (clientId !== znClientId) {
      setCookie(CookiesEnum.ZN_CLIENT_ID, clientId, {
        days: 365,
        domain: 'domain',
      });
    }

    CookieHandler.znClientId = clientId;

    return clientId;
  }

  /**
   * Generate a new GA cookie and set it in the browser - used to bypass adblockers/cookie restrictions
   * Since we are using this cookie for more than just analytics, it is imperative that it is set
   * code sample from: analytics.js release 2017-09-21
   */
  private static generateGaCookie(): string {
    if (!!CookieHandler.znClientId) return CookieHandler.znClientId;

    const randomGen = () => {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      const random = array[0] & 2147483647;

      return Number(random.toString().slice(0, 10));
    };

    const hashString = (str: string) => {
      let b = 1;
      let c: number;

      if (str) {
        for (b = 0, c = str.length - 1; 0 <= c; c--) {
          let d = str.charCodeAt(c);
          b = ((b << 6) & 268435455) + d + (d << 14);
          d = b & 266338304;
          b = 0 !== d ? b ^ (d >> 21) : b;
        }
        return b;
      }

      return 1;
    };

    const generateRandomClientId = () => {
      let a =
        window.navigator.userAgent +
        (document.cookie ? document.cookie : '') +
        (document.referrer ? document.referrer : '');
      let b = a.length;
      let c = window.history.length;

      for (a; 0 < c; ) {
        a += c-- ^ b++;
      }

      return [randomGen() ^ (hashString(a) & 2147483647), Math.round(new Date().getTime() / 1e3)].join('.');
    };

    const clientId = generateRandomClientId();
    const cookie = `GA1.1.${clientId}`;

    CookieHandler.znClientId = cookie;

    // set the _ga cookie
    [CookiesEnum.GA_CLIENT_ID, CookiesEnum.ZN_CLIENT_ID].forEach((cookieName) => {
      setCookie(cookieName, cookie, {
        days: 365,
        domain: 'domain',
      });
    });

    // because we are setting the cookie, we need to set the new visitor type
    setCookie(CookiesEnum.ZN_NEW_VISITOR, 'true', { days: 0, hours: 0.5, domain: 'subDomain' }); // new visitor type (nvt)

    setTimeout(() => {
      TelemetryRunner.addGtagLoadedFunction(() => {}); // add empty function to gtag loaded -> trigger check for gtag loaded
      TelemetrySyncSession.getInstance().syncIntegrations();
    }, 2000);

    return cookie;
  }
}
